import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { CheckCircle2, Users, Calendar, Heart } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Join = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
    email: '',
    phone: '',
    school: '',
    tribe: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    interests: '',
    whyJoin: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      age: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/members`, formData);
      toast({
        title: "Application Submitted!",
        description: "Thank you for your interest in joining Naiwa Daughters. We'll be in touch soon!",
      });
      setFormData({
        firstName: '',
        lastName: '',
        age: '',
        email: '',
        phone: '',
        school: '',
        tribe: '',
        parentName: '',
        parentEmail: '',
        parentPhone: '',
        interests: '',
        whyJoin: ''
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your application. Please try again.",
        variant: "destructive"
      });
    }
  };

  const requirements = [
    'American Indian or Alaska Native, or Canadian Indigenous woman',
    'Ages 10-17 years old',
    'Officially identified as a member of a federally recognized Indian tribe/Nation',
    'Commitment to attend at least 2 NAIWA fundraisers per year',
    'Commitment to attend at least 2 Naiwa Daughters events per year',
    'Commitment to attend at least 4 monthly meetings per year'
  ];

  const benefits = [
    { icon: Users, text: 'Connect with other Indigenous young women' },
    { icon: Heart, text: 'Develop leadership and advocacy skills' },
    { icon: Calendar, text: 'Participate in cultural activities and events' },
    { icon: CheckCircle2, text: 'Build confidence and public speaking abilities' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: '#53a5ca' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Join Naiwa Daughters
            </h1>
            <p className="text-lg md:text-xl leading-relaxed opacity-95">
              Become part of a growing community of young Indigenous women leaders. Together, we're building a brighter future for our communities.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#7e2f22' }}>Why Join Naiwa Daughters?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg text-center">
                    <CardContent className="pt-8 pb-8">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#53a5ca20' }}>
                        <IconComponent className="h-8 w-8" style={{ color: '#53a5ca' }} />
                      </div>
                      <p className="text-gray-700 font-medium">{benefit.text}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Membership Requirements */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl" style={{ color: '#7e2f22' }}>Membership Requirements</CardTitle>
                <CardDescription>To become an active member of Naiwa Daughters, you must meet the following criteria:</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#53a5ca' }} />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl" style={{ color: '#7e2f22' }}>Membership Application</CardTitle>
                <CardDescription>Fill out the form below to apply for membership. We'll review your application and get back to you soon!</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: '#7e2f22' }}>Personal Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="age">Age *</Label>
                        <Select onValueChange={handleSelectChange} value={formData.age}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your age" />
                          </SelectTrigger>
                          <SelectContent>
                            {[10, 11, 12, 13, 14, 15, 16, 17].map((age) => (
                              <SelectItem key={age} value={age.toString()}>{age}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="school">School *</Label>
                        <Input id="school" name="school" value={formData.school} onChange={handleChange} required />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="tribe">Tribe/Nation Affiliation *</Label>
                        <Input id="tribe" name="tribe" value={formData.tribe} onChange={handleChange} placeholder="e.g., Eastern Band of Cherokee Indians" required />
                      </div>
                    </div>
                  </div>

                  {/* Parent/Guardian Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: '#7e2f22' }}>Parent/Guardian Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="parentName">Parent/Guardian Name *</Label>
                        <Input id="parentName" name="parentName" value={formData.parentName} onChange={handleChange} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="parentEmail">Parent/Guardian Email *</Label>
                        <Input id="parentEmail" name="parentEmail" type="email" value={formData.parentEmail} onChange={handleChange} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="parentPhone">Parent/Guardian Phone *</Label>
                        <Input id="parentPhone" name="parentPhone" type="tel" value={formData.parentPhone} onChange={handleChange} required />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: '#7e2f22' }}>Tell Us About Yourself</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="interests">What are your interests and hobbies?</Label>
                        <Textarea id="interests" name="interests" value={formData.interests} onChange={handleChange} rows={3} placeholder="Tell us what you're passionate about..." />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="whyJoin">Why do you want to join Naiwa Daughters? *</Label>
                        <Textarea id="whyJoin" name="whyJoin" value={formData.whyJoin} onChange={handleChange} rows={4} placeholder="Share your motivation for joining..." required />
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full text-white hover:opacity-90 transition-all duration-200"
                    style={{ backgroundColor: '#53a5ca' }}
                  >
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Join;